import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Music, Search, List, Play, Pause, Radio, Check, X, Shuffle, Settings, RefreshCw, RotateCcw, Trash2, Volume2 } from 'lucide-react';
import * as api from './api';
import config from './config';
import AdminPage from './AdminPage';
import { screenAfterReadyAuth } from './authNavigation';
import { AddTrackRow, QueueTrackRow } from './TrackRows';
import { PlaylistTracks } from './PlaylistTracks';
import { BackButton, QueueNowPlayingCard } from './NavigationControls';
import { createSearchRequestTracker, isNearScrollEnd, mergeUniqueTracks, nextSearchOffset } from './searchPagination';
import {
  addUriToPlaybackQueue,
  createSpotifyRequest,
  isRecoverableDeviceError,
  playTracksOnSdkDevice,
  restartCurrentTrack,
  runPlaybackAttempt,
  skipCurrentTrackWithConfirmation,
  summarizeSdkPlaybackState,
  shouldStartPlayback,
  transferPlaybackToDevice
} from './spotifyPlayback';
import './App.css';

// Utility function to format duration
const formatDuration = (ms) => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <span className="toast-icon">
        {type === 'success' ? <Check size={24} /> : <X size={24} />}
      </span>
      <span className="toast-message">{message}</span>
    </div>
  );
};

function KioskApp() {
  const [currentScreen, setCurrentScreen] = useState('loading'); // loading, auth, home, admin, etc.
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [queue, setQueue] = useState([]);
  const [trackPosition, setTrackPosition] = useState(0);
  
  // Toast state
  const [toast, setToast] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [searchEndMessage, setSearchEndMessage] = useState('');
  
  // Playlists state
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [playlistError, setPlaylistError] = useState('');
  
  // Genre state
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genreTracks, setGenreTracks] = useState([]);
  
  // Artist Radio state
  const [artistQuery, setArtistQuery] = useState('');
  const [artists, setArtists] = useState([]);
  const [artistRadioTracks, setArtistRadioTracks] = useState([]);
  
  // Background playlist state - simplified
  const [backgroundName, setBackgroundName] = useState(null);
  const [userSongCount, setUserSongCount] = useState(0);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminAuthenticated, setAdminAuthenticated] = useState(false);
  
  const playerRef = useRef(null);
  const playerInitializingRef = useRef(false);
  const playerReadyRef = useRef(false);
  const deviceIdRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const searchTimeoutRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchRequestTrackerRef = useRef(createSearchRequestTracker());
  const authCheckInFlightRef = useRef(false);
  const spotifyReadyRef = useRef(false);
  const positionIntervalRef = useRef(null);
  const spotifyRequestRef = useRef(null);
  const skipConfirmationRef = useRef(null);
  const skipInFlightRef = useRef(false);

  if (!spotifyRequestRef.current) {
    spotifyRequestRef.current = createSpotifyRequest({
      getAccessToken: api.getAccessToken,
      refreshAccessToken: api.forceRefreshToken,
      getSdkState: () => ({ ready: playerReadyRef.current, deviceId: deviceIdRef.current })
    });
  }
  const spotifyRequest = spotifyRequestRef.current;
  const getSdkDevice = () => ({ ready: playerReadyRef.current, deviceId: deviceIdRef.current });

  // Show toast notification
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  // ============================================
  // INITIALIZATION & AUTHENTICATION
  // ============================================

  useEffect(() => {
    // Load Spotify SDK
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    script.onerror = () => {
      if (spotifyReadyRef.current) setCurrentScreen('playerUnavailable');
    };
    document.body.appendChild(script);

    const sdkTimeout = setTimeout(() => {
      if (!window.Spotify && spotifyReadyRef.current) setCurrentScreen('playerUnavailable');
    }, 10000);

    window.onSpotifyWebPlaybackSDKReady = () => {
      console.log('✅ Spotify SDK Ready');
      checkAuth();
    };

    checkAuth();
    const authPoll = setInterval(checkAuth, 10000);
    
    return () => {
      clearInterval(authPoll);
      clearTimeout(sdkTimeout);
      searchAbortRef.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (positionIntervalRef.current) {
        clearInterval(positionIntervalRef.current);
      }
      playerReadyRef.current = false;
      deviceIdRef.current = null;
      playerRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    if (authCheckInFlightRef.current) return;
    authCheckInFlightRef.current = true;
    try {
      const status = await api.checkAuthStatus();

      if (status.state === 'ready') {
        spotifyReadyRef.current = true;
        const tokenData = await api.getAccessToken();
        if (tokenData.access_token && !playerRef.current && !playerInitializingRef.current && window.Spotify) {
          initializePlayer();
        }
        if (window.Spotify || playerRef.current) {
          setCurrentScreen(screenAfterReadyAuth);
        }
        return;
      }

      spotifyReadyRef.current = false;
      setCurrentScreen(status.state === 'reauth_required' ? 'maintenance' : 'loading');
    } catch (error) {
      spotifyReadyRef.current = false;
      setCurrentScreen('backendUnavailable');
    } finally {
      authCheckInFlightRef.current = false;
    }
  };

  // ============================================
  // PLAYER INITIALIZATION & MANAGEMENT
  // ============================================

  const initializePlayer = () => {
    if (playerRef.current || playerInitializingRef.current) return;
    playerInitializingRef.current = true;
    console.log('🎵 Initializing Spotify player...');
    
    const spotifyPlayer = new window.Spotify.Player({
      name: config.PLAYER_NAME,
      getOAuthToken: async (cb) => {
        try {
          const tokenData = await api.getAccessToken();
          cb(tokenData.access_token);
        } catch (error) {
          console.error('Failed to get token for player:', error);
          cb('');
          await checkAuth();
        }
      },
      volume: 0.8
    });

    playerRef.current = spotifyPlayer;

    spotifyPlayer.addListener('ready', ({ device_id }) => {
      console.log('✅ Player Ready with Device ID:', device_id);
      deviceIdRef.current = device_id;
      playerReadyRef.current = true;
      setDeviceId(device_id);
      setPlayerReady(true);
      reconnectAttempts.current = 0;
    });

    spotifyPlayer.addListener('not_ready', ({ device_id }) => {
      console.log('⚠️ Device has gone offline:', device_id);
      if (deviceIdRef.current !== device_id) return;
      deviceIdRef.current = null;
      playerReadyRef.current = false;
      setDeviceId(null);
      setPlayerReady(false);
      attemptReconnect();
    });

    spotifyPlayer.addListener('player_state_changed', async (state) => {
      if (!state) {
        console.log('⚠️ Player state is null');
        return;
      }
      
      const newTrack = state.track_window?.current_track;
      if (!newTrack?.uri) {
        console.warn('Spotify SDK state did not include a playable current track.');
        setIsPlaying(false);
        return;
      }

      const pendingSkip = skipConfirmationRef.current?.observe(state);
      if (pendingSkip) pendingSkip.catch(() => { /* skipNext reports confirmation failures */ });

      // Just update UI state - Spotify handles auto-advance
      setCurrentTrack({
        name: newTrack.name,
        artist: newTrack.artists?.map(a => a?.name).filter(Boolean).join(', ') || 'Unknown artist',
        album: newTrack.album?.name || '',
        image: newTrack.album?.images?.[0]?.url,
        duration: newTrack.duration_ms,
        position: state.position,
        uri: newTrack.uri
      });
      setTrackPosition(state.position);
      setIsPlaying(!state.paused);
    });

    // Handle errors
    spotifyPlayer.addListener('initialization_error', ({ message }) => {
      console.error('❌ Initialization error:', message);
      deviceIdRef.current = null;
      playerReadyRef.current = false;
      if (playerRef.current === spotifyPlayer) playerRef.current = null;
      setDeviceId(null);
      setPlayerReady(false);
      setPlayer(null);
      showToast('Player initialization failed', 'error');
    });

    spotifyPlayer.addListener('authentication_error', async ({ message }) => {
      console.error('❌ Authentication error:', message);
      deviceIdRef.current = null;
      playerReadyRef.current = false;
      setDeviceId(null);
      setPlayerReady(false);
      let refreshed = false;
      try {
        await api.forceRefreshToken();
        refreshed = true;
      } catch (error) { /* checkAuth maps permanent and temporary failures for the kiosk. */ }
      if (refreshed) await attemptReconnect();
      await checkAuth();
    });

    spotifyPlayer.addListener('account_error', ({ message }) => {
      console.error('❌ Account error:', message);
      showToast('Spotify Premium required', 'error');
    });

    spotifyPlayer.addListener('playback_error', ({ message }) => {
      console.error('❌ Playback error:', message);
      attemptReconnect();
    });

    spotifyPlayer.addListener('autoplay_failed', () => {
      console.error('Spotify autoplay was blocked by the browser.');
      showToast('Tap Play to start the music.', 'error');
    });

    spotifyPlayer.connect().then(success => {
      if (success) {
        console.log('✅ Player connected successfully');
      } else {
        console.error('❌ Player connection failed');
        if (playerRef.current === spotifyPlayer) playerRef.current = null;
        setPlayer(null);
      }
    }).catch(error => {
      console.error('❌ Player connection failed:', error);
      if (playerRef.current === spotifyPlayer) playerRef.current = null;
      setPlayer(null);
    }).finally(() => {
      playerInitializingRef.current = false;
    });

    setPlayer(spotifyPlayer);
  };

  // Activate only the current SDK device, with bounded recoverable retries.
  const activateDevice = async (device_id, retries = 3) => {
    const attempts = Math.max(1, Math.min(3, retries));
    for (let i = 0; i < attempts; i++) {
      if (!playerReadyRef.current || deviceIdRef.current !== device_id) return false;
      try {
        console.log(`🔄 Activating device (attempt ${i + 1}/${attempts})...`);
        await transferPlaybackToDevice(spotifyRequest, device_id, {
          flow: 'sdk_ready_activation', attempt: i + 1
        });
        console.log('✅ Device activated successfully!');
        return true;
      } catch (error) {
        console.error(`❌ Activation attempt ${i + 1} error:`, error.diagnostic || error.message);
        if (!isRecoverableDeviceError(error)) return false;
      }
      if (i + 1 < attempts) await new Promise(resolve => setTimeout(resolve, 700));
    }
    console.log('⚠️ Could not activate device after retries');
    return false;
  };

  // Attempt to reconnect player
  const attemptReconnect = async () => {
    if (reconnectAttempts.current >= 3) {
      console.log('❌ Max reconnect attempts reached');
      showToast('Connection lost. Please refresh.', 'error');
      return;
    }
    
    reconnectAttempts.current++;
    console.log(`🔄 Attempting to reconnect (${reconnectAttempts.current}/3)...`);
    
    if (playerRef.current) {
      try {
        playerReadyRef.current = false;
        deviceIdRef.current = null;
        setPlayerReady(false);
        setDeviceId(null);
        await playerRef.current.disconnect();
        await new Promise(resolve => setTimeout(resolve, 1000));
        const connected = await playerRef.current.connect();
        if (connected) {
          console.log('✅ Reconnected successfully');
          reconnectAttempts.current = 0;
        }
      } catch (error) {
        console.error('❌ Reconnect failed:', error);
      }
    }
  };

  // ============================================
  // POSITION TRACKING (updates every second)
  // ============================================

  useEffect(() => {
    // Clear existing interval
    if (positionIntervalRef.current) {
      clearInterval(positionIntervalRef.current);
    }

    // Only track position when playing
    if (isPlaying && currentTrack) {
      positionIntervalRef.current = setInterval(() => {
        setTrackPosition(prev => {
          const newPosition = prev + 1000;
          // Don't exceed duration
          if (currentTrack.duration && newPosition >= currentTrack.duration) {
            return currentTrack.duration;
          }
          return newPosition;
        });
      }, 1000);
    }

    return () => {
      if (positionIntervalRef.current) {
        clearInterval(positionIntervalRef.current);
      }
    };
  }, [isPlaying, currentTrack]);

  // Sync position when we get player state updates
  useEffect(() => {
    if (currentTrack?.position !== undefined) {
      setTrackPosition(currentTrack.position);
    }
  }, [currentTrack?.position]);

  // ============================================
  // PLAYBACK CONTROLS
  // ============================================

  const togglePlayPause = async () => {
    if (!player) return;
    try {
      await player.activateElement();
      await player.togglePlay();
    } catch (error) {
      console.error('Spotify SDK play/pause failed:', error?.message || error);
      showToast('Playback control failed. Please try again.', 'error');
    }
  };

  const restartCurrentSong = async () => {
    try {
      await restartCurrentTrack(playerRef.current || player);
      setTrackPosition(0);
    } catch (error) {
      console.error('Spotify SDK restart failed:', error?.message || error);
      showToast('Could not restart this track.', 'error');
    }
  };

  const skipNext = async () => {
    if (skipInFlightRef.current) return;
    const activePlayer = playerRef.current || player;
    if (!activePlayer) return;
    skipInFlightRef.current = true;
    const queueBefore = {
      length: queue.length,
      firstUri: queue[0]?.uri || null,
      firstQueueItemId: queue[0]?.queueItemId || null
    };
    try {
      const initialState = await activePlayer.getCurrentState();
      const initial = summarizeSdkPlaybackState(initialState);
      console.info('Spotify SDK Skip attempt', {
        currentTrackUri: initial.currentTrackUri,
        deviceId: deviceIdRef.current,
        sdkReady: playerReadyRef.current,
        nextTrackCount: initial.nextTrackCount,
        skippingDisallowed: initial.skippingDisallowed,
        logicalQueueBefore: queueBefore
      });

      const result = await skipCurrentTrackWithConfirmation({
        player: activePlayer,
        initialState,
        onConfirmed: () => api.shiftQueue(),
        onConfirmationReady: confirmation => { skipConfirmationRef.current = confirmation; }
      });
      const shifted = result.confirmation;
      const nextQueue = Array.isArray(shifted?.queue) ? shifted.queue : [];
      setQueue(nextQueue);
      console.info('Spotify SDK Skip confirmed', {
        currentTrackUriBefore: result.beforeUri,
        currentTrackUriAfter: result.afterUri,
        deviceId: deviceIdRef.current,
        sdkReady: playerReadyRef.current,
        skipCommandResult: 'resolved_and_confirmed',
        stateChecks: result.stateChecks,
        logicalQueueBefore: queueBefore,
        logicalQueueAfter: {
          length: nextQueue.length,
          firstUri: nextQueue[0]?.uri || null,
          firstQueueItemId: nextQueue[0]?.queueItemId || null
        }
      });
    } catch (error) {
      console.error('Spotify SDK Skip failed', {
        ...(error?.diagnostic || {}),
        deviceId: deviceIdRef.current,
        sdkReady: playerReadyRef.current,
        logicalQueueBefore: queueBefore
      });
      showToast('Could not skip this track.', 'error');
    } finally {
      skipConfirmationRef.current = null;
      skipInFlightRef.current = false;
    }
  };

  // Play ALL tracks in queue (enables Spotify auto-advance)
  const playQueue = async (tracks, startIndex = 0) => {
    console.log(`▶️ Playing ${tracks.length} tracks, starting at index ${startIndex}`);
    const outcome = await runPlaybackAttempt(() => playTracksOnSdkDevice({
      spotifyRequest,
      getDevice: getSdkDevice,
      recoverDevice: attemptReconnect,
      tracks,
      startIndex
    }), {
      onSuccess: () => console.log('✅ Queue playing!'),
      onFailure: error => {
        console.error('Play queue error:', error.diagnostic || error.message);
        const message = error.status === 429
          ? `Spotify is busy. Try again in ${error.retryAfter || 'a few'} seconds.`
          : error.code === 'sdk_device_unavailable'
            ? 'Player loading... Please wait.'
            : 'Failed to start playback';
        showToast(message, 'error');
      }
    });
    return outcome.ok;
  };

  const addTrackToQueue = async (track) => {
    try {
      // This begins in the user's click event and satisfies browser autoplay policies.
      await playerRef.current?.activateElement();

      // Add to our backend queue
      const response = await api.addToQueue(track);
      if (Array.isArray(response?.queue)) setQueue(response.queue);
      
      if (shouldStartPlayback({ currentTrack, isPlaying })) {
        // Nothing playing - start with this track
        console.log('▶️ Starting with:', track.name);
        const started = await playQueue([track], 0);
        if (started) showToast(`Now playing "${track.name}"`, 'success');
        return started;
      } else {
        // Something playing - add to Spotify's queue (plays next)
        try {
          await addUriToPlaybackQueue({
            spotifyRequest,
            getDevice: getSdkDevice,
            recoverDevice: attemptReconnect,
            uri: track.uri
          });
          showToast(`Added "${track.name}" - plays next!`, 'success');
          return true;
        } catch (e) {
          console.error('Spotify queue add failed:', e.diagnostic || e.message);
          const message = e.status === 429
            ? `Saved in the jukebox queue. Spotify is busy; retry after ${e.retryAfter || 'a few'} seconds.`
            : 'Saved in the jukebox queue, but Spotify could not queue it.';
          showToast(message, 'error');
          return false;
        }
      }
    } catch (error) {
      console.error('Queue error:', error);
      showToast('Failed to add to queue', 'error');
      return false;
    }
  };

  const removeTrackFromQueue = async (track, index) => {
    try {
      const response = await api.removeFromQueue(index, track.queueItemId);
      setQueue(Array.isArray(response?.queue) ? response.queue : []);
      if (Number.isInteger(response?.userSongCount)) setUserSongCount(response.userSongCount);
      showToast(`Removed "${track.name}" from the queue`, 'success');
      return true;
    } catch (error) {
      console.error('Queue remove error:', error);
      showToast('Could not remove that song from the queue', 'error');
      return false;
    }
  };

  // ============================================
  // SEARCH
  // ============================================

  const loadSearchPage = async (query, offset, generation, append) => {
    if (!searchRequestTrackerRef.current.tryStart(query, generation)) return;
    append ? setIsLoadingMore(true) : setIsSearching(true);
    const controller = new AbortController();
    searchAbortRef.current = controller;
    try {
      const page = await api.searchTracks(query, offset, controller.signal);
      if (!searchRequestTrackerRef.current.isCurrent(query, generation)) return;
      setSearchResults(previous => append ? mergeUniqueTracks(previous, page.tracks || []) : mergeUniqueTracks([], page.tracks || []));
      setSearchOffset(nextSearchOffset(page));
      setSearchHasMore(Boolean(page.hasMore));
      setSearchEndMessage(page.hasMore ? '' : 'End of results');
    } catch (error) {
      if (error.code !== 'ERR_CANCELED' && error.name !== 'CanceledError') {
        console.error('Search error:', error);
        showToast(append ? 'Could not load more results' : 'Search failed', 'error');
      }
    } finally {
      if (searchRequestTrackerRef.current.isCurrent(query, generation)) {
        setIsSearching(false);
        setIsLoadingMore(false);
      }
      searchRequestTrackerRef.current.finish(generation);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const generation = searchRequestTrackerRef.current.reset(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchAbortRef.current?.abort();
    setSearchResults([]);
    setSearchOffset(0);
    setSearchHasMore(false);
    setSearchEndMessage('');
    setIsLoadingMore(false);

    if (!query.trim()) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => loadSearchPage(query.trim(), 0, generation, false), config.SEARCH_DEBOUNCE_MS);
  };

  const loadMoreSearchResults = () => {
    if (!searchHasMore || isSearching || isLoadingMore || searchRequestTrackerRef.current.isLoading() || !searchQuery.trim()) return;
    loadSearchPage(searchQuery.trim(), searchOffset, searchRequestTrackerRef.current.currentGeneration(), true);
  };

  // ============================================
  // PLAYLISTS
  // ============================================

  const loadPlaylists = async () => {
    try {
      console.log('Loading playlists...');
      const userPlaylists = await api.getUserPlaylists();
      console.log('Playlists loaded:', userPlaylists.length);
      setPlaylists(userPlaylists);
      if (userPlaylists.length === 0) {
        showToast('No playlists found', 'error');
      }
    } catch (error) {
      console.error('Error loading playlists:', error);
      showToast('Failed to load playlists', 'error');
    }
  };

  const loadPlaylistTracks = async (playlistId) => {
    setPlaylistTracks([]);
    setPlaylistError('');
    try {
      const tracks = await api.getPlaylistTracks(playlistId);
      setPlaylistTracks(tracks);
    } catch (error) {
      console.error('Error loading playlist tracks:', error);
      const message = error.response?.data?.message || 'This playlist could not be loaded.';
      setPlaylistError(message);
      showToast('Failed to load playlist', 'error');
    }
  };

  // Start a playlist as background music (shuffled)
  const startBackgroundPlaylist = async (playlist) => {
    try {
      showToast(`Loading "${playlist.name}"...`, 'success');
      
      // Load playlist into queue (backend shuffles it)
      const response = await api.setBackgroundPlaylist(playlist.id, playlist.name);
      
      console.log('🎵 Loaded:', response.trackCount, 'tracks');
      
      setBackgroundName(response.playlistName);
      
      // Get full queue from backend
      const queueData = await api.getQueue();
      setQueue(queueData.queue);
      
      // Play ALL tracks - Spotify will auto-advance
      if (queueData.queue.length > 0) {
        const started = await playQueue(queueData.queue, 0);
        if (!started) return;
      }
      
      showToast(`🎵 Playing "${playlist.name}" (${response.trackCount} songs)`, 'success');
      
      setSelectedPlaylist(null);
      setCurrentScreen('home');
    } catch (error) {
      console.error('Error loading playlist:', error);
      showToast('Failed to load playlist', 'error');
    }
  };

  // Clear queue (admin function)
  const stopBackground = async () => {
    try {
      await api.clearQueue();
      setQueue([]);
      setBackgroundName(null);
      setUserSongCount(0);
      showToast('Queue cleared', 'success');
    } catch (error) {
      console.error('Error clearing queue:', error);
    }
  };

  // Legacy in-memory admin screen handlers remain only for safe fallback;
  // normal navigation uses the dedicated /admin entry point.
  const handleAdminLogin = async () => {
    try {
      await api.adminLogin(adminPassword);
      setAdminAuthenticated(true);
      setAdminPassword('');
    } catch (error) {
      showToast('Incorrect password', 'error');
      setAdminPassword('');
    }
  };

  const adminClearAll = async () => {
    try {
      await api.adminClearAll();
      setQueue([]);
      setBackgroundName(null);
      setUserSongCount(0);
      showToast('Queue cleared', 'success');
    } catch (error) { showToast('Failed to clear queue', 'error'); }
  };

  const adminReconnectSpotify = attemptReconnect;
  const adminActivateDevice = () => activateDevice(deviceId);
  const adminReloadPage = () => window.location.reload();

  // ============================================
  // GENRES
  // ============================================

  const loadGenreTracks = async (genre) => {
    try {
      const tracks = await api.getGenreTracks(genre);
      setGenreTracks(tracks);
      setSelectedGenre(genre);
    } catch (error) {
      console.error('Error loading genre tracks:', error);
      showToast('Failed to load genre', 'error');
    }
  };

  // ============================================
  // ARTIST RADIO
  // ============================================

  const searchArtists = async (query) => {
    setArtistQuery(query);
    
    if (!query.trim()) {
      setArtists([]);
      return;
    }
    
    try {
      const results = await api.searchArtists(query);
      setArtists(results);
    } catch (error) {
      console.error('Artist search error:', error);
    }
  };

  const loadArtistRadio = async (artistId) => {
    if (!artistId) {
      showToast('Invalid artist selected', 'error');
      return;
    }
    
    try {
      console.log('Loading artist radio for:', artistId);
      const tracks = await api.getArtistRadio(artistId);
      setArtistRadioTracks(tracks);
    } catch (error) {
      console.error('Error loading artist radio:', error);
      showToast('Failed to load artist radio', 'error');
    }
  };

  // ============================================
  // QUEUE
  // ============================================
  // QUEUE
  // ============================================

  const loadQueue = async () => {
    try {
      const response = await api.getQueue();
      setQueue(response.queue);
      setBackgroundName(response.backgroundName);
      setUserSongCount(response.userSongCount);
    } catch (error) {
      console.error('Error loading queue:', error);
    }
  };

  useEffect(() => {
    const updateKiosk = async () => {
      try {
        await api.reportPlayerStatus({ ready: playerReady, deviceId, currentTrack: currentTrack?.name || null });
        const command = await api.getPlayerCommand();
        if (command?.type === 'reconnect') await attemptReconnect();
        if (command?.type === 'activate' && deviceId) await activateDevice(deviceId);
        if (command?.type === 'reload') window.location.reload();
      } catch (error) {
        // Authentication status polling owns the visible backend-offline state.
      }
    };
    updateKiosk();
    const timer = setInterval(updateKiosk, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerReady, deviceId, currentTrack?.name]);

  // ============================================
  // RENDER - LOADING SCREEN
  // ============================================

  if (currentScreen === 'loading') {
    return (
      <div className="screen auth-screen">
        <div className="auth-content">
          <div className="auth-icon">🎵</div>
          <h1>Tom's Jukebox</h1>
          <p>Starting Jukebox...</p>
        </div>
      </div>
    );
  }

  if (currentScreen === 'maintenance') {
    return (
      <div className="screen auth-screen">
        <div className="auth-content maintenance-card">
          <div className="auth-icon">🎵</div>
          <h1>Music is temporarily unavailable.</h1>
          <p>Please try again shortly.</p>
        </div>
      </div>
    );
  }

  if (currentScreen === 'backendUnavailable') {
    return (
      <div className="screen auth-screen">
        <div className="auth-content maintenance-card">
          <div className="auth-icon">⚠️</div>
          <h1>Jukebox server unavailable.</h1>
          <p>Checking the connection automatically...</p>
        </div>
      </div>
    );
  }

  if (currentScreen === 'playerUnavailable') {
    return (
      <div className="screen auth-screen">
        <div className="auth-content maintenance-card">
          <div className="auth-icon">⚠️</div>
          <h1>The music player could not start.</h1>
          <p>The jukebox will keep checking automatically.</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - ADMIN SCREEN
  // ============================================

  if (currentScreen === 'admin') {
    return (
      <div className="screen admin-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        {/* Header - always visible with back button */}
        <div className="header-with-back">
          <BackButton onClick={() => {
            // Blur any focused input to dismiss keyboard
            if (document.activeElement) document.activeElement.blur();
            setCurrentScreen('home'); 
            setAdminAuthenticated(false); 
            setAdminPassword(''); 
          }} />
          <h1>{adminAuthenticated ? 'Admin Panel' : 'Admin Login'}</h1>
          <div className="spacer"></div>
        </div>

        {!adminAuthenticated ? (
          <div className="admin-login-container">
            <div className="admin-login-content">
              <Settings size={80} />
              <h2>Enter Password</h2>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                className="admin-password-input"
                placeholder="Password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
              />
              <button className="admin-login-button" onClick={handleAdminLogin}>
                Enter
              </button>
            </div>
          </div>
        ) : (
          <div className="admin-content">
            <div className="admin-buttons">
              <button className="admin-action-button red" onClick={adminClearAll}>
                <Trash2 size={48} />
                <span className="admin-button-title">Clear All Queues</span>
                <span className="admin-button-desc">Remove all songs from queue and stop background playlist</span>
              </button>

              <button className="admin-action-button blue" onClick={adminReconnectSpotify}>
                <RefreshCw size={48} />
                <span className="admin-button-title">Reconnect Spotify</span>
                <span className="admin-button-desc">Disconnect and reconnect the player</span>
              </button>

              <button className="admin-action-button green" onClick={adminActivateDevice}>
                <Volume2 size={48} />
                <span className="admin-button-title">Activate Device</span>
                <span className="admin-button-desc">Set playback to this jukebox</span>
              </button>

              <button className="admin-action-button purple" onClick={adminReloadPage}>
                <RefreshCw size={48} />
                <span className="admin-button-title">Reload Page</span>
                <span className="admin-button-desc">Refresh the entire application</span>
              </button>
            </div>

            {/* Queue control */}
            {queue.length > 0 && (
              <div className="admin-background-control">
                <h3>Queue Active</h3>
                <p>{queue.length} songs ({userSongCount} priority, {queue.length - userSongCount} background)</p>
                <button className="stop-background-button" onClick={stopBackground}>
                  <X size={24} />
                  Clear Queue
                </button>
              </div>
            )}

            {/* Status info */}
            <div className="admin-status">
              <h3>Status</h3>
              <p>Player Ready: {playerReady ? '✅ Yes' : '❌ No'}</p>
              <p>Device ID: {deviceId ? deviceId.substring(0, 8) + '...' : 'None'}</p>
              <p>Queue: {queue.length} songs</p>
              <p>Background: {backgroundName || 'None'}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // RENDER - HOME SCREEN
  // ============================================

  if (currentScreen === 'home') {
    return (
      <div className="screen home-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header">
          <h1>🎵 Tom's Jukebox</h1>
          {!playerReady && (
            <p className="status-loading">⏳ Connecting to Spotify...</p>
          )}
          {playerReady && (
            <p className="status-ready">✓ Ready to play!</p>
          )}
        </div>

        {currentTrack && (
          <div className="now-playing-banner">
            <button 
              className="now-playing-banner-button"
              onClick={() => setCurrentScreen('nowPlaying')}
            >
              <div className="track-image">
                {currentTrack.image ? (
                  <img src={currentTrack.image} alt="Album art" />
                ) : (
                  <Music size={48} />
                )}
              </div>
              <div className="track-info">
                <p className="label">NOW PLAYING - TAP TO VIEW</p>
                <h2>{currentTrack.name}</h2>
                <p>{currentTrack.artist}</p>
              </div>
            </button>
            <button 
              className="control-button-separate" 
              onClick={togglePlayPause}
            >
              {isPlaying ? <Pause size={40} /> : <Play size={40} />}
            </button>
          </div>
        )}

        <div className="menu-grid">
          <button className="menu-button green" onClick={() => setCurrentScreen('search')}>
            <Search size={80} />
            <span className="title">SEARCH</span>
            <span className="subtitle">Find Any Song</span>
          </button>

          <button className="menu-button blue" onClick={() => { setCurrentScreen('playlists'); loadPlaylists(); }}>
            <List size={80} />
            <span className="title">MY PLAYLISTS</span>
            <span className="subtitle">Saved Collections</span>
          </button>

          {/* HIDDEN FOR NOW - Genre and Artist Radio
          <button className="menu-button purple" onClick={() => setCurrentScreen('genre')}>
            <Grid size={80} />
            <span className="title">BROWSE GENRE</span>
            <span className="subtitle">By Music Style</span>
          </button>

          <button className="menu-button pink" onClick={() => setCurrentScreen('artistRadio')}>
            <Radio size={80} />
            <span className="title">ARTIST RADIO</span>
            <span className="subtitle">Similar Artists</span>
          </button>
          */}
        </div>

        {/* Queue indicator */}
        {backgroundName && (
          <div className="background-indicator">
            <Shuffle size={20} />
            <span>Playing: {backgroundName}</span>
            <span className="remaining">({(queue || []).length} in queue)</span>
          </div>
        )}

        <div className="bottom-nav">
          <button 
            className="nav-button purple" 
            onClick={() => setCurrentScreen('nowPlaying')}
            disabled={!currentTrack}
          >
            <Music size={32} />
            Now Playing
          </button>
          <button className="nav-button yellow" onClick={() => { setCurrentScreen('queue'); loadQueue(); }}>
            <List size={32} />
            View Queue ({(queue || []).length})
          </button>
        </div>

        {/* Admin button - small, at the bottom */}
        <button 
          className="admin-button-small"
          onClick={() => { window.location.href = '/admin'; }}
        >
          <Settings size={16} />
          Admin
        </button>
      </div>
    );
  }

  // ============================================
  // RENDER - NOW PLAYING SCREEN
  // ============================================

  if (currentScreen === 'nowPlaying') {
    return (
      <div className="screen now-playing-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => setCurrentScreen('home')} />
          <h1>Now Playing</h1>
          <div className="spacer"></div>
        </div>

        <div className="now-playing-content">
          {currentTrack ? (
            <>
              <div className="album-art-large">
                {currentTrack.image ? (
                  <img src={currentTrack.image} alt="Album art" />
                ) : (
                  <Music className="placeholder-icon" size={120} />
                )}
              </div>

              <div className="track-info-large">
                <h2 className="track-title">{currentTrack.name}</h2>
                <p className="track-artist">{currentTrack.artist}</p>
                <p className="track-album">{currentTrack.album}</p>
              </div>

              <div className="progress-section">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: currentTrack.duration ? 
                        `${(trackPosition / currentTrack.duration) * 100}%` : 
                        '0%' 
                    }}
                  ></div>
                </div>
                <div className="progress-time">
                  <span>{formatDuration(trackPosition)}</span>
                  <span>{formatDuration(currentTrack.duration || 0)}</span>
                </div>
              </div>

              <div className="playback-controls-large">
                <button
                  type="button"
                  className="restart-button-large"
                  onClick={restartCurrentSong}
                  disabled={!playerReady}
                  aria-label="Restart Song"
                >
                  <RotateCcw size={22} strokeWidth={2.25} aria-hidden="true" />
                  <span>Restart</span>
                </button>
                <button 
                  type="button"
                  className="control-button-large" 
                  onClick={togglePlayPause}
                  disabled={!playerReady}
                >
                  {isPlaying ? <Pause size={60} /> : <Play size={60} />}
                </button>
                <button 
                  type="button"
                  className="skip-button-large" 
                  onClick={skipNext}
                  disabled={!playerReady}
                >
                  Skip
                </button>
              </div>

              <div className="queue-info">
                <button 
                  className="queue-link-button"
                  onClick={() => { setCurrentScreen('queue'); loadQueue(); }}
                >
                  <List size={32} />
                  <span>View Queue ({queue.length} songs)</span>
                </button>
              </div>
            </>
          ) : (
            <div className="no-track-playing">
              <Music size={120} />
              <h2>No song playing</h2>
              <p>Search for music to get started</p>
              <button 
                className="start-button"
                onClick={() => setCurrentScreen('search')}
              >
                <Search size={40} />
                Find Music
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - SEARCH SCREEN
  // ============================================

  if (currentScreen === 'search') {
    const goBack = () => {
      searchRequestTrackerRef.current.reset('');
      searchAbortRef.current?.abort();
      setSearchQuery('');
      setSearchResults([]);
      setCurrentScreen('home');
    };
    
    return (
      <div className="screen search-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={goBack} />
          <h1>Search Music</h1>
          <div className="spacer"></div>
        </div>

        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Type song or artist name..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
          <Search className="search-icon" size={48} />
        </div>

        <div className="results-container" onScroll={event => {
          if (isNearScrollEnd(event.currentTarget)) loadMoreSearchResults();
        }}>
          {isSearching && searchResults.length === 0 && <div className="loading">Searching...</div>}
          {(searchResults || []).map((track, idx) => (
            <AddTrackRow key={track.id || idx} track={track} index={idx} onAdd={addTrackToQueue} formatDuration={formatDuration} />
          ))}
          {isLoadingMore && <div className="loading loading-more">Loading more songs...</div>}
          {!isSearching && searchResults.length > 0 && searchEndMessage && (
            <div className="search-end-state">{searchEndMessage}</div>
          )}
          {searchHasMore && !isLoadingMore && searchResults.length > 0 && (
            <button className="load-more-button" onClick={loadMoreSearchResults}>Load More Songs</button>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - PLAYLISTS SCREEN
  // ============================================

  if (currentScreen === 'playlists' && !selectedPlaylist) {
    return (
      <div className="screen playlists-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => setCurrentScreen('home')} />
          <h1>My Playlists</h1>
          <div className="spacer"></div>
        </div>

        <div className="playlists-grid">
          {(playlists || []).map((playlist, idx) => (
            <button
              key={playlist.id || idx}
              className="playlist-card"
              onClick={() => { setSelectedPlaylist(playlist); loadPlaylistTracks(playlist.id); }}
            >
              <div className="playlist-image">
                {playlist.image ? (
                  <img src={playlist.image} alt={playlist.name} loading="lazy" />
                ) : (
                  <Music size={64} />
                )}
              </div>
              <div className="playlist-info">
                <h3>{playlist.name}</h3>
                <p>{playlist.tracks_count} songs</p>
              </div>
              <div className="play-icon">
                <Play size={32} />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - PLAYLIST TRACKS SCREEN
  // ============================================

  if (currentScreen === 'playlists' && selectedPlaylist) {
    return (
      <div className="screen playlist-tracks-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back playlist-detail-header">
          <BackButton onClick={() => setSelectedPlaylist(null)} />
          <h1 className="playlist-detail-title">Playlist: {selectedPlaylist.name}</h1>
          <div className="spacer"></div>
        </div>

        {/* Shuffle Play Button */}
        <div className="shuffle-button-container">
          <button 
            className="shuffle-play-button"
            onClick={() => startBackgroundPlaylist(selectedPlaylist)}
          >
            <Shuffle size={32} />
            <span>Shuffle Play as Background Music</span>
          </button>
          <p className="shuffle-hint">Songs added by guests will play first, then back to this playlist</p>
        </div>

        <PlaylistTracks
          key={selectedPlaylist.id}
          tracks={playlistTracks}
          error={playlistError}
          onAdd={addTrackToQueue}
          formatDuration={formatDuration}
        />
      </div>
    );
  }

  // ============================================
  // RENDER - GENRE SELECTION SCREEN
  // ============================================

  if (currentScreen === 'genre' && !selectedGenre) {
    const genres = [
      { name: 'rock', emoji: '🎸', color: 'red' },
      { name: 'pop', emoji: '🎤', color: 'pink' },
      { name: 'jazz', emoji: '🎷', color: 'blue' },
      { name: 'country', emoji: '🤠', color: 'amber' },
      { name: 'blues', emoji: '🎵', color: 'indigo' },
      { name: 'soul', emoji: '💜', color: 'purple' },
      { name: 'r&b', emoji: '💖', color: 'rose' },
      { name: 'folk', emoji: '🌿', color: 'green' },
      { name: 'disco', emoji: '🕺', color: 'fuchsia' },
    ];

    return (
      <div className="screen genre-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => setCurrentScreen('home')} />
          <h1>Browse by Genre</h1>
          <div className="spacer"></div>
        </div>

        <div className="genre-grid">
          {genres.map((genre, idx) => (
            <button
              key={idx}
              className={`genre-button ${genre.color}`}
              onClick={() => { loadGenreTracks(genre.name); setCurrentScreen('genreResults'); }}
            >
              <span className="emoji">{genre.emoji}</span>
              <span className="name">{genre.name.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - GENRE RESULTS SCREEN
  // ============================================

  if (currentScreen === 'genreResults') {
    return (
      <div className="screen genre-results-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => { setCurrentScreen('genre'); setSelectedGenre(null); }} />
          <h1>{selectedGenre?.toUpperCase()} Music</h1>
          <div className="spacer"></div>
        </div>

        <div className="results-container">
          {genreTracks.map((track, idx) => (
            <AddTrackRow key={`${track.id || track.uri}-${idx}`} track={track} index={idx} onAdd={addTrackToQueue} formatDuration={formatDuration} showNumber />
          ))}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - ARTIST RADIO SEARCH SCREEN
  // ============================================

  if (currentScreen === 'artistRadio') {
    return (
      <div className="screen artist-radio-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => setCurrentScreen('home')} />
          <h1>Artist Radio</h1>
          <div className="spacer"></div>
        </div>

        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Type artist name..."
            value={artistQuery}
            onChange={(e) => searchArtists(e.target.value)}
            autoFocus
          />
          <Search className="search-icon" size={48} />
        </div>

        <div className="results-container">
          {artists.map((artist, idx) => (
            <div key={artist.id || idx} className="artist-item">
              <div className="artist-image">
                {artist.image ? (
                  <img src={artist.image} alt={artist.name} loading="lazy" />
                ) : (
                  <span className="emoji">🎤</span>
                )}
              </div>
              <div className="artist-details">
                <h3>{artist.name}</h3>
                <p>{artist.genres?.slice(0, 2).join(', ') || 'Artist'}</p>
              </div>
              <button className="radio-button" onClick={() => { loadArtistRadio(artist.id); setCurrentScreen('artistRadioResults'); }}>
                <Radio size={32} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - ARTIST RADIO RESULTS SCREEN
  // ============================================

  if (currentScreen === 'artistRadioResults') {
    return (
      <div className="screen artist-radio-results-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => setCurrentScreen('artistRadio')} />
          <h1>Artist Radio</h1>
          <div className="spacer"></div>
        </div>

        <div className="results-container">
          {artistRadioTracks.map((track, idx) => (
            <AddTrackRow key={`${track.id || track.uri}-${idx}`} track={track} index={idx} onAdd={addTrackToQueue} formatDuration={formatDuration} />
          ))}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - QUEUE SCREEN
  // ============================================

  if (currentScreen === 'queue') {
    return (
      <div className="screen queue-screen">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="header-with-back">
          <BackButton onClick={() => setCurrentScreen('home')} />
          <h1>Up Next</h1>
          <div className="spacer"></div>
        </div>

        <QueueNowPlayingCard track={currentTrack} onOpen={() => setCurrentScreen('nowPlaying')} />

        <div className="queue-header">
          <h3>Queue ({(queue || []).length} songs)</h3>
          <p className="queue-hint">Songs play in order</p>
        </div>

        <div className="results-container">
          {(queue || []).map((track, idx) => (
            <QueueTrackRow key={track.queueItemId || `${track.id || track.uri}-${idx}`} track={track} index={idx}
              onRemove={removeTrackFromQueue} formatDuration={formatDuration} />
          ))}
          {(!queue || queue.length === 0) && (
            <div className="empty-state">
              <p>No songs in queue</p>
              <p>Add some music to get started!</p>
            </div>
          )}
        </div>

        {/* Queue info */}
        {backgroundName && (
          <div className="background-mini-indicator">
            <Shuffle size={20} />
            <span>{backgroundName} ({(queue || []).length} songs)</span>
          </div>
        )}

        <div className="bottom-nav">
          <button className="nav-button green" onClick={() => setCurrentScreen('search')}>
            <Search size={32} />
            Add More Songs
          </button>
        </div>
      </div>
    );
  }

  // Fallback - return to home
  return (
    <div className="screen home-screen">
      <p style={{padding: '20px', color: 'white'}}>Loading...</p>
      <button onClick={() => setCurrentScreen('home')} style={{padding: '20px', margin: '20px'}}>
        Go Home
      </button>
    </div>
  );
}

function App() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  return pathname === '/admin' ? <AdminPage /> : <KioskApp />;
}

export default App;
